import { useIsFocused } from '@react-navigation/native';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useAuth } from '../../../context/Auth';
import Card from '../../../components/Card';
import DataTableContainer from '../../../components/DataTableContainer';
import { dispatchService } from '../../../services/central-api/dispatch';
import { translate } from '../../../services/translate';
import { applyMask } from '../../../components/helpers/documents';
import MyDataTable from '../../../components/MyDataTable';
import moment from 'moment';
import { View } from 'react-native';

interface Props {
    unitId: number;
}

export default function UnitFormPolicePatrolSchedule({ unitId }: Props) {
    const { user: authenticatedUser } = useAuth();

    const isFocused = useIsFocused();

    const policePatrolQuery = useQuery({
        queryKey: ['getPolicePatrolSchedulesForUnit', unitId],
        queryFn: () => dispatchService.getPolicePatrolSchedulesForUnit(unitId!),
        enabled: !!authenticatedUser && !!unitId && isFocused,
    });

    const { data: policePatrolScheduleData } = policePatrolQuery;

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && policePatrolQuery.isFetching;

    function getActivitiesTableForData(localData: ActivityAndService[], simplified: boolean) {
        return <MyDataTable
            columns={[
                ...(!simplified ? [{
                    name: 'ID',
                    cell: (row: ActivityAndService) => localData ? localData.findIndex(item => item.id === row.id) + 1 : '',
                    grow: 0,
                }] : []),
                {
                    name: translate('indicator'),
                    selector: row => row.indicator.code,
                    wrap: true,
                },
                ...(!simplified ? [{
                    name: translate('cep'),
                    selector: (row: ActivityAndService) => applyMask(row.cep, 'cep'),
                    wrap: true,
                }] : []),
                ...(!simplified ? [{
                    name: translate('address'),
                    selector: (row: ActivityAndService) => row.address,
                }] : []),
                ...(!simplified ? [{
                    name: translate('number'),
                    selector: (row: ActivityAndService) => row.number,
                    grow: 0,
                }] : []),
                ...(!simplified ? [{
                    name: translate('startTime'),
                    selector: (row: ActivityAndService) => moment(row.startDate).format('DD/MM/YYYY HH:mm'),
                }] : []),
                ...(!simplified ? [{
                    name: translate('endTime'),
                    selector: (row: ActivityAndService) => moment(row.endDate).format('DD/MM/YYYY HH:mm'),
                }] : []),
                {
                    name: translate('actions'),
                    selector: row => row.actionsQty,
                },
                {
                    name: translate('audience'),
                    selector: row => row.publicQty,
                },
                {
                    name: translate('document'),
                    selector: row => row.documentNumber,
                },
            ]}
            data={localData}
            progressPending={isLoading}
        />;
    }

    return (
        <Card style={{ flexDirection: 'column', gap: 16, padding: 0 }}>
            <View style={{ flex: 2 }}>
                <DataTableContainer
                    dataTable={<MyDataTable
                        expandableRows
                        expandableRowDisabled={row => (!row.activityAndServices || !row.activityAndServices.length)}
                        expandableRowsComponent={row => <View style={{ padding: 8 }}>
                            {getActivitiesTableForData(row.data.activityAndServices, false)}
                        </View>}
                        columns={[
                            {
                                name: 'ID',
                                cell: row => policePatrolScheduleData ? policePatrolScheduleData.findIndex(item => item.id === row.id) + 1 : '',
                                grow: 0,
                            },
                            {
                                name: translate('activity'),
                                selector: row => row.activity.code,
                                wrap: true,
                            },
                            {
                                name: translate('cep'),
                                selector: row => applyMask(row.cep, 'cep'),
                                wrap: true,
                            },
                            {
                                name: translate('address'),
                                selector: row => row.address,
                            },
                            {
                                name: translate('number'),
                                selector: row => row.number,
                                grow: 0,
                            },
                            {
                                name: translate('odometer'),
                                selector: row => row.odometer ?? '',
                            },
                            {
                                name: translate('startTime'),
                                selector: row => moment(row.startDate).format('DD/MM/YYYY HH:mm'),
                            },
                            {
                                name: translate('endTime'),
                                selector: row => row.endDate ? moment(row.endDate).format('DD/MM/YYYY HH:mm') : '',
                            },
                            {
                                name: translate('observations'),
                                selector: row => row.observations ?? '',
                            },
                        ]}
                        data={policePatrolScheduleData || []}
                        progressPending={isLoading}
                    />}
                />
            </View>
        </Card>
    );
}
